import { normalizeLangKey } from '../lib/localeLangKey';

const I = (label, icon, hubRoute) => ({ label, icon, ...(hubRoute ? { hubRoute } : {}) });

/** MingoIcon registry.v1 — 15개 대분류 Tabler 키 */
export const CATEGORY_ICON_KEYS = {
  food_cafe_mart: 'category_food_cafe_mart',
  living_realestate_rental: 'category_living_realestate_rental',
  home_interior: 'category_home_interior',
  travel_activity_experience: 'category_travel_activity_experience',
  accommodation_stay: 'category_accommodation_stay',
  jobs_business: 'category_jobs_business',
  professional_services: 'category_professional_services',
  education_lesson: 'category_education_lesson',
  beauty_wellness: 'category_beauty_wellness',
  health_medical: 'category_health_medical',
  mobility_transport: 'category_mobility_transport',
  telecom: 'category_telecom',
  finance_remittance: 'category_finance_remittance',
  wedding_event: 'category_wedding_event',
  pet: 'category_pet',
  creator_media: 'category_creator_media',
  parenting_care: 'category_parenting_care',
};

/** 카테고리별 파스텔 아이콘 스타일 (registry.v1와 동기화) */
export const CATEGORY_ICON_STYLE = {
  travel_activity_experience: { bg: '#EDE9FE', color: '#7C3AED' },
  accommodation_stay: { bg: '#FCE7F3', color: '#DB2777' },
  living_realestate_rental: { bg: '#FFEDD5', color: '#EA580C' },
  food_cafe_mart: { bg: '#FEF3C7', color: '#D97706' },
  home_interior: { bg: '#DCFCE7', color: '#16A34A' },
  education_lesson: { bg: '#DBEAFE', color: '#2563EB' },
  beauty_wellness: { bg: '#FCE7F3', color: '#DB2777' },
  health_medical: { bg: '#FEE2E2', color: '#DC2626' },
  mobility_transport: { bg: '#EDE9FE', color: '#7C3AED' },
  telecom: { bg: '#CFFAFE', color: '#0891B2' },
  finance_remittance: { bg: '#DCFCE7', color: '#059669' },
  wedding_event: { bg: '#FDF4FF', color: '#A21CAF' },
  pet: { bg: '#FFEDD5', color: '#EA580C' },
  creator_media: { bg: '#FCE7F3', color: '#DB2777' },
  parenting_care: { bg: '#DBEAFE', color: '#2563EB' },
  jobs_business: { bg: '#F1F5F9', color: '#475569' },
  professional_services: { bg: '#E0E7FF', color: '#4338CA' },
};

/** 카테고리별 아이콘 원형 배경 (AllServices 상세 항목 카드) */
export const CATEGORY_ITEM_ICON_BG = Object.fromEntries(
  Object.entries(CATEGORY_ICON_STYLE).map(([id, s]) => [id, s.bg]),
);

/** 15개 카테고리 — id = All Services / DB businesses.category 그룹 id (업체 대분류 SSOT) */
export const ALL_CATEGORIES = [
  {
    id: 'food_cafe_mart',
    name: { ko: '음식·카페·마트', en: 'Food, Cafe & Mart', vi: 'Ẩm thực, Cà phê & Siêu thị', ru: 'Еда, кафе и маркет', ja: 'グルメ・カフェ・スーパー', 'zh-CN': '美食·咖啡·超市', 'zh-TW': '美食·咖啡·超市', hi: 'खाना, कैफ़े और मार्ट', th: 'อาหาร คาเฟ่ มาร์ท', id: 'Makanan, kafe & mart' },
    emoji: '🍽️',
    description: '현지 맛집·한식·카페·배달·마트',
    subgroups: [
      {
        name: '음식·음료',
        items: [
          I('한식', '🍚', '/hub/food_cafe_mart'),
          I('일식', '🍱', '/hub/food_cafe_mart'),
          I('양식', '🍝', '/hub/food_cafe_mart'),
          I('중식', '🥢', '/hub/food_cafe_mart'),
          I('베트남식', '🌏', '/hub/food_cafe_mart'),
          I('카페', '☕', '/hub/food_cafe_mart'),
          I('베이커리', '🥐', '/hub/food_cafe_mart'),
        ],
      },
      {
        name: '마트·배달',
        items: [
          I('마트', '🛒', '/hub/food_cafe_mart'),
          I('편의점', '🏬', '/hub/food_cafe_mart'),
          I('배달', '🛵', '/hub/food_cafe_mart'),
          I('공동구매', '👥', '/hub/food_cafe_mart'),
        ],
      },
    ],
  },
  {
    id: 'living_realestate_rental',
    name: { ko: '부동산·임대', en: 'Real Estate & Rental', vi: 'Bất động sản & Cho thuê', ru: 'Недвижимость и аренда', ja: '不動産・賃貸', 'zh-CN': '房产·租赁', 'zh-TW': '房產·租賃', hi: 'रियल एस्टेट और किराया', th: 'อสังหาฯ และเช่า', id: 'Properti & sewa' },
    emoji: '🏠',
    description: '부동산·월세·전세·매매·가전렌탈·승계',
    subgroups: [
      {
        name: '매물·거래',
        items: [
          I('아파트', '🏗️', '/real-estate'),
          I('빌라', '🏘️', '/real-estate'),
          I('단독주택', '🏡', '/real-estate'),
          I('상가', '🏬', '/real-estate'),
          I('오피스', '💼', '/real-estate'),
          I('월세', '🏠', '/real-estate'),
          I('전세', '🏡', '/real-estate'),
          I('매매', '🤝', '/real-estate'),
          I('가전렌탈', '📺', '/real-estate'),
        ],
      },
      {
        name: '승계·기타',
        items: [
          I('집승계', '🔑', '/succession'),
          I('사무실승계', '🗝️', '/succession'),
        ],
      },
    ],
  },
  {
    id: 'home_interior',
    name: { ko: '홈·인테리어', en: 'Home & Interior', vi: 'Nhà ở & Nội thất', ru: 'Дом и интерьер', ja: 'ホーム・インテリア', 'zh-CN': '家居·装修', 'zh-TW': '家居·裝修', hi: 'घर और इंटीरियर', th: 'บ้านและอินทีเรียร์', id: 'Rumah & interior' },
    emoji: '🔨',
    description: '도배, 장판, 인테리어·시공, 홈케어',
    subgroups: [
      {
        name: '홈·인테리어',
        items: [
          I('청소', '🧹', '/hub/home_interior'),
          I('이사', '📦', '/hub/home_interior'),
          I('수리', '🔧', '/hub/home_interior'),
          I('인테리어', '🛋️', '/hub/home_interior'),
          I('에어컨청소', '❄️', '/hub/home_interior'),
          I('커튼블라인드', '🪟', '/hub/home_interior'),
          I('장판', '🪵', '/hub/home_interior'),
          I('타일', '⬜', '/hub/home_interior'),
          I('도배', '🖼️', '/hub/home_interior'),
          I('가구조립', '🪑', '/hub/home_interior'),
          I('방역소독', '🦠', '/hub/home_interior'),
          I('정원조경', '🌳', '/hub/home_interior'),
        ],
      },
    ],
  },
  {
    id: 'travel_activity_experience',
    name: { ko: '여행·투어', en: 'Travel & Tours', vi: 'Du lịch & Tour', ru: 'Путешествия и туры', ja: '旅行・ツアー', 'zh-CN': '旅行·观光', 'zh-TW': '旅行·觀光', hi: 'यात्रा और टूर', th: 'ท่องเที่ยวและทัวร์', id: 'Wisata & tur' },
    emoji: '✈️',
    description: '투어·액티비티·예약승계',
    subgroups: [
      {
        name: '여행·체험',
        items: [
          I('단품투어', '🗺️', '/hub/travel_activity_experience'),
          I('패키지', '📦', '/hub/travel_activity_experience'),
          I('데이투어', '☀️', '/hub/travel_activity_experience'),
          I('골프', '⛳', '/hub/travel_activity_experience'),
          I('액티비티', '🏄', '/hub/travel_activity_experience'),
          I('체험', '🎭', '/hub/travel_activity_experience'),
          I('픽업드롭', '🚐', '/hub/travel_activity_experience'),
          I('공항픽업', '✈️', '/hub/travel_activity_experience'),
          I('렌터카', '🚙', '/hub/travel_activity_experience'),
          I('크루즈', '🚢', '/hub/travel_activity_experience'),
        ],
      },
      {
        name: '승계·예약',
        items: [
          I('호텔승계', '🔑', '/transfer'),
          I('골프예약승계', '⛳', '/transfer'),
          I('투어승계', '🗺️', '/transfer'),
        ],
      },
    ],
  },
  {
    id: 'accommodation_stay',
    name: { ko: '호텔·스테이', en: 'Hotels & Stays', vi: 'Khách sạn & Lưu trú', ru: 'Отели и проживание', ja: 'ホテル・ステイ', 'zh-CN': '酒店·住宿', 'zh-TW': '飯店·住宿', hi: 'होटल और ठहराव', th: 'โรงแรมและที่พัก', id: 'Hotel & menginap' },
    emoji: '🏨',
    description: '호텔·리조트·풀빌라·게스트하우스',
    subgroups: [
      {
        name: '숙박',
        items: [
          I('호텔', '🏨', '/hub/accommodation_stay'),
          I('리조트', '🌴', '/hub/accommodation_stay'),
          I('풀빌라', '🏊', '/hub/accommodation_stay'),
          I('게스트하우스', '🛏️', '/hub/accommodation_stay'),
          I('홈스테이', '🏡', '/hub/accommodation_stay'),
          I('단기숙박', '📅', '/hub/accommodation_stay'),
          I('펜션', '🏠', '/hub/accommodation_stay'),
        ],
      },
    ],
  },
  {
    id: 'jobs_business',
    name: { ko: '구인구직', en: 'Jobs & Business', vi: 'Việc làm & Kinh doanh', ru: 'Работа и бизнес', ja: '求人・求職', 'zh-CN': '招聘求职', 'zh-TW': '徵才求職', hi: 'नौकरियाँ', th: 'หางาน', id: 'Lowongan kerja' },
    emoji: '💼',
    description: '구인구직·프리랜서·인턴',
    subgroups: [
      {
        name: '구인구직',
        items: [
          I('구인', '💼', '/hub/jobs_business'),
          I('구직', '👤', '/hub/jobs_business'),
          I('프리랜서', '🖥️', '/hub/jobs_business'),
          I('인턴', '🎓', '/hub/jobs_business'),
        ],
      },
    ],
  },
  {
    id: 'professional_services',
    name: { ko: '비자·법률·회계·행정', en: 'Visa, Legal & Accounting', vi: 'Visa, Pháp lý & Kế toán', ru: 'Виза, право и бухгалтерия', ja: 'ビザ・法律・会計', 'zh-CN': '签证·法律·会计', 'zh-TW': '簽證·法律·會計', hi: 'वीज़ा, क़ानून, अकाउंटिंग', th: 'วีซ่า กฎหมาย บัญชี', id: 'Visa, hukum & akuntansi' },
    emoji: '⚖️',
    description: '비자·법률상담·세무회계·법인설립·번역통역',
    subgroups: [
      {
        name: '비자·법률·회계·행정',
        items: [
          I('비자', '📋', '/hub/professional_services'),
          I('법률상담', '⚖️', '/hub/professional_services'),
          I('세무·회계', '📊', '/hub/professional_services'),
          I('법인설립', '🏢', '/hub/professional_services'),
          I('행정대행', '📑', '/hub/professional_services'),
          I('번역', '🌐', '/hub/professional_services'),
          I('통역', '🗣️', '/hub/professional_services'),
          I('공증', '🖋️', '/hub/professional_services'),
        ],
      },
    ],
  },
  {
    id: 'education_lesson',
    name: { ko: '교육·레슨', en: 'Education & Lessons', vi: 'Giáo dục & Học tập', ru: 'Образование и уроки', ja: '教育・レッスン', 'zh-CN': '教育·课程', 'zh-TW': '教育·課程', hi: 'शिक्षा और कक्षा', th: 'การศึกษาและคลาส', id: 'Pendidikan & les' },
    emoji: '🎓',
    description: '학교·레슨·클래스·체험',
    subgroups: [
      {
        name: '교육·레슨',
        items: [
          I('한국어', '🇰🇷', '/hub/education_lesson'),
          I('영어', '🇬🇧', '/hub/education_lesson'),
          I('베트남어', '🇻🇳', '/hub/education_lesson'),
          I('수학', '🔢', '/hub/education_lesson'),
          I('음악', '🎵', '/hub/education_lesson'),
          I('미술', '🎨', '/hub/education_lesson'),
          I('체육', '⚽', '/hub/education_lesson'),
          I('요리', '👨‍🍳', '/hub/education_lesson'),
          I('온라인', '💻', '/hub/education_lesson'),
          I('오프라인', '🏫', '/hub/education_lesson'),
          I('1:1', '👤', '/hub/education_lesson'),
          I('그룹', '👥', '/hub/education_lesson'),
        ],
      },
    ],
  },
  {
    id: 'beauty_wellness',
    name: { ko: '뷰티·웰니스', en: 'Beauty & Wellness', vi: 'Làm đẹp & Chăm sóc', ru: 'Красота и велнес', ja: 'ビューティー・ウェルネス', 'zh-CN': '美妆·养生', 'zh-TW': '美妝·養生', hi: 'ब्यूटी और वेलनेस', th: 'บิวตี้และเวลเนส', id: 'Kecantikan & wellness' },
    emoji: '💇',
    description: '미용실·이발소·네일·왁싱·마사지·스파·헬스',
    subgroups: [
      {
        name: '뷰티·웰니스',
        items: [
          I('미용실', '💇', '/hub/beauty_wellness'),
          I('이발소', '💈', '/hub/beauty_wellness'),
          I('네일', '💅', '/hub/beauty_wellness'),
          I('왁싱', '✨', '/hub/beauty_wellness'),
          I('마사지', '💆', '/hub/beauty_wellness'),
          I('스파', '🧖', '/hub/beauty_wellness'),
          I('피부관리', '🧴', '/hub/beauty_wellness'),
          I('헬스장', '🏋️', '/hub/beauty_wellness'),
        ],
      },
    ],
  },
  {
    id: 'health_medical',
    name: { ko: '의료·건강', en: 'Medical & Health', vi: 'Y tế & Sức khỏe', ru: 'Медицина и здоровье', ja: '医療・健康', 'zh-CN': '医疗·健康', 'zh-TW': '醫療·健康', hi: 'चिकित्सा और स्वास्थ्य', th: 'การแพทย์และสุขภาพ', id: 'Medis & kesehatan' },
    emoji: '🏥',
    description: '내과·치과·피부과·성형·한의원·약국',
    subgroups: [
      {
        name: '의료·건강',
        items: [
          I('내과', '🩺', '/hub/health_medical'),
          I('치과', '🦷', '/hub/health_medical'),
          I('피부과', '✨', '/hub/health_medical'),
          I('성형', '💉', '/hub/health_medical'),
          I('한의원', '🌿', '/hub/health_medical'),
          I('약국', '💊', '/hub/health_medical'),
          I('건강검진', '🩻', '/hub/health_medical'),
        ],
      },
    ],
  },
  {
    id: 'mobility_transport',
    name: { ko: '모빌리티/이사/용달', en: 'Mobility, Moving & Delivery', vi: 'Di chuyển & Chuyển nhà', ru: 'Транспорт, переезд и доставка', ja: '移動・引越し・配送', 'zh-CN': '出行·搬家·配送', 'zh-TW': '出行·搬家·配送', hi: 'आवागमन, मूविंग, डिलीवरी', th: 'เดินทาง ย้ายบ้าน ส่งของ', id: 'Mobilitas, pindahan & kirim' },
    emoji: '🚗',
    description: '렌탈·이사·택배·차량 매매·정비',
    subgroups: [
      {
        name: '모빌리티/이사/용달',
        items: [
          I('택시', '🚕', '/hub/mobility_transport'),
          I('오토바이택시', '🛵', '/hub/mobility_transport'),
          I('이사', '📦', '/hub/mobility_transport'),
          I('용달', '🚐', '/hub/mobility_transport'),
          I('자동차렌탈', '🚙', '/hub/mobility_transport'),
          I('오토바이렌탈', '🛵', '/hub/mobility_transport'),
          I('자전거렌탈', '🚲', '/hub/mobility_transport'),
          I('공항픽업', '✈️', '/hub/mobility_transport'),
          I('셔틀', '🚌', '/hub/mobility_transport'),
        ],
      },
    ],
  },
  {
    id: 'telecom',
    name: { ko: '통신·유심·인터넷', en: 'Telecom, SIM & Internet', vi: 'Viễn thông, SIM & Internet', ru: 'Связь, SIM и интернет', ja: '通信・SIM・ネット', 'zh-CN': '通讯·SIM·网络', 'zh-TW': '通訊·SIM·網路', hi: 'टेलीकॉम, सिम, इंटरनेट', th: 'โทรคม SIM อินเทอร์เน็ต', id: 'Telekom, SIM & internet' },
    emoji: '📶',
    description: '유심·이심·휴대폰개통·인터넷설치·포켓와이파이',
    subgroups: [
      {
        name: '통신·유심·인터넷',
        items: [
          I('유심·이심', '📶', '/hub/telecom'),
          I('휴대폰개통', '📱', '/hub/telecom'),
          I('인터넷·와이파이설치', '🌐', '/hub/telecom'),
          I('포켓와이파이', '📡', '/hub/telecom'),
          I('휴대폰매장·중고폰', '🏪', '/hub/telecom'),
          I('통신사대리점', '🏬', '/hub/telecom'),
          I('수리·AS', '🔧', '/hub/telecom'),
        ],
      },
    ],
  },
  {
    id: 'finance_remittance',
    name: { ko: '금융·송금', en: 'Finance & Remittance', vi: 'Tài chính & Chuyển tiền', ru: 'Финансы и переводы', ja: '金融・送金', 'zh-CN': '金融·汇款', 'zh-TW': '金融·匯款', hi: 'वित्त और रेमिटेंस', th: 'การเงินและโอนเงิน', id: 'Keuangan & remitansi' },
    emoji: '💰',
    description: '환전·송금·보험·세금',
    subgroups: [
      {
        name: '금융·송금',
        items: [
          I('한국송금', '🇰🇷', '/hub/finance_remittance'),
          I('베트남송금', '🇻🇳', '/hub/finance_remittance'),
          I('환전', '💱', '/hub/finance_remittance'),
          I('대출', '🏦', '/hub/finance_remittance'),
          I('보험', '🛡️', '/hub/finance_remittance'),
        ],
      },
    ],
  },
  {
    id: 'wedding_event',
    name: { ko: '웨딩·이벤트', en: 'Wedding & Events', vi: 'Cưới hỏi & Sự kiện', ru: 'Свадьбы и мероприятия', ja: 'ウェディング・イベント', 'zh-CN': '婚礼·活动', 'zh-TW': '婚禮·活動', hi: 'शादी और इवेंट', th: 'แต่งงานและอีเวนต์', id: 'Pernikahan & acara' },
    emoji: '🎉',
    description: '웨딩·이벤트·사진·영상',
    subgroups: [
      {
        name: '웨딩·이벤트',
        items: [
          I('웨딩촬영', '📸', '/hub/wedding_event'),
          I('웨딩드레스', '👗', '/hub/wedding_event'),
          I('메이크업', '💄', '/hub/wedding_event'),
          I('스드메', '💒', '/hub/wedding_event'),
          I('허니문', '🌴', '/hub/wedding_event'),
          I('돌잔치', '🎂', '/hub/wedding_event'),
          I('생일파티', '🎉', '/hub/wedding_event'),
          I('기업행사', '🏢', '/hub/wedding_event'),
        ],
      },
    ],
  },
  {
    id: 'pet',
    name: { ko: '반려동물', en: 'Pets', vi: 'Thú cưng', ru: 'Питомцы', ja: 'ペット', 'zh-CN': '宠物', 'zh-TW': '寵物', hi: 'पालतू जानवर', th: 'สัตว์เลี้ยง', id: 'Hewan peliharaan' },
    emoji: '🐾',
    description: '동물병원·펫미용·펫시터·용품',
    subgroups: [
      {
        name: '반려동물',
        items: [
          I('동물병원', '🏥', '/hub/pet'),
          I('미용', '✂️', '/hub/pet'),
          I('호텔', '🏨', '/hub/pet'),
          I('훈련', '🐕', '/hub/pet'),
          I('용품', '🛍️', '/hub/pet'),
          I('분양', '🐾', '/hub/pet'),
          I('사료배달', '📦', '/hub/pet'),
        ],
      },
    ],
  },
  {
    id: 'creator_media',
    name: { ko: '크리에이터·미디어', en: 'Creator & Media', vi: 'Sáng tạo & Truyền thông', ru: 'Креатив и медиа', ja: 'クリエイター・メディア', 'zh-CN': '创作者·媒体', 'zh-TW': '創作者·媒體', hi: 'क्रिएटर और मीडिया', th: 'ครีเอเตอร์และสื่อ', id: 'Kreator & media' },
    emoji: '📱',
    description: '라이브·웹툰·유튜브·크리에이터',
    subgroups: [
      {
        name: '크리에이터·미디어',
        items: [
          I('사진작가', '📷', '/hub/creator_media'),
          I('영상제작', '🎬', '/hub/creator_media'),
          I('SNS마케팅', '📱', '/hub/creator_media'),
          I('광고대행', '📣', '/hub/creator_media'),
          I('유튜브', '▶️', '/hub/creator_media'),
          I('인스타', '📸', '/hub/creator_media'),
        ],
      },
    ],
  },
  {
    id: 'parenting_care',
    name: { ko: '육아·돌봄', en: 'Parenting & Care', vi: 'Nuôi dạy con & Chăm sóc', ru: 'Дети и уход', ja: '育児・ケア', 'zh-CN': '育儿·照护', 'zh-TW': '育兒·照護', hi: 'पालन-पोषण और देखभाल', th: 'เลี้ยงลูกและดูแล', id: 'Pengasuhan & perawatan' },
    emoji: '👶',
    description: '베이비시터·산후도우미·아이돌봄·놀이방',
    subgroups: [
      {
        name: '육아·돌봄',
        items: [
          I('베이비시터', '👶', '/hub/parenting_care'),
          I('산후도우미', '🤱', '/hub/parenting_care'),
          I('어린이집정보', '🏫', '/hub/parenting_care'),
          I('유아용품', '🍼', '/hub/parenting_care'),
          I('아동교육', '📚', '/hub/parenting_care'),
          I('키즈카페', '🎠', '/hub/parenting_care'),
          I('소아과', '🩺', '/hub/parenting_care'),
        ],
      },
    ],
  },
];

/** SSOT: 14 hub major ids (`businesses.category`). Re-export — do not hardcode elsewhere. */
export const HUB_MAJOR_IDS = ALL_CATEGORIES.map((c) => c.id);

/** @type {ReadonlySet<string>} */
export const HUB_MAJOR_ID_SET = new Set(HUB_MAJOR_IDS);

/** @param {string|null|undefined} id */
export function isHubMajorCategory(id) {
  return HUB_MAJOR_ID_SET.has(String(id || '').trim());
}

/** @param {string|null|undefined} id */
export function getHubMajorCategory(id) {
  const key = String(id || '').trim();
  return ALL_CATEGORIES.find((c) => c.id === key) || null;
}

/**
 * Resolve major category display name (string legacy or {ko,en,vi,ru,ja,...}).
 * @param {string|{ko?:string,en?:string,vi?:string,ru?:string}|{name?: any}} catOrName
 * @param {string} [language]
 */
export function resolveCategoryDisplayName(catOrName, language = 'ko') {
  const lang = normalizeLangKey(language);
  if (catOrName == null) return '';
  let name = catOrName;
  if (typeof catOrName === 'object' && catOrName.name != null) name = catOrName.name;
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const picked = name[lang] || name.en || name.ko || name.vi || name.ru || '';
    return String(picked || '').trim() || String(name.ko || '').trim();
  }
  return String(name || '');
}

/** businesses.category id → display label + icon */
export const CATEGORY_META = Object.fromEntries(
  ALL_CATEGORIES.map((c) => [
    c.id,
    {
      label: resolveCategoryDisplayName(c, 'ko'),
      emoji: c.emoji,
      iconKey: CATEGORY_ICON_KEYS[c.id] || `category_${c.id}`,
    },
  ]),
);

export const FOOD_MART_HUB_ROUTE = '/hub/food_cafe_mart';
export const FOOD_MART_ITEM_LABEL = '음식·카페·마트';
export const MINGO_SERVICE_FAVORITES_KEY = 'mingo_favorites';

const ITEM_ICON_KEY_SEP = '\x1f';

/** 예전 라벨(음식점·맛집 등) → 맛집·카페·마트 허브로 통일 */
const LEGACY_FOOD_MART_LABELS = new Set([
  '음식·카페',
  '음식점',
  '맛집',
  '맛집/마트',
  '맛집/카페',
  '맛집·음식',
  '맛집/식당',
]);

/** 구형 카탈로그 id → 13그룹 id (즐겨찾기·URL 호환) */
const LEGACY_CATEGORY_KEY_MAP = {
  life: 'food_cafe_mart',
  travel: 'travel_activity_experience',
  realestate: 'living_realestate_rental',
  freelance: 'jobs_business',
  interior: 'home_interior',
  repair: 'home_interior',
  jobs: 'jobs_business',
  sports: 'beauty_wellness',
  business: 'jobs_business',
  medical: 'health_medical',
  health_medical_beauty: 'health_medical',
  education: 'education_lesson',
  vehicle: 'mobility_transport',
  finance: 'finance_remittance',
  community: 'creator_media',
  class: 'education_lesson',
  expat: 'jobs_business',
  religion: 'education_lesson',
  creator: 'creator_media',
  pet: 'pet',
  parenting: 'parenting_care',
};

export function isLegacyFoodMartLabel(label) {
  const s = String(label || '').trim();
  if (!s || s === FOOD_MART_ITEM_LABEL) return false;
  return LEGACY_FOOD_MART_LABELS.has(s);
}

function normalizeCategoryKey(categoryKey) {
  const k = String(categoryKey || '').trim();
  return LEGACY_CATEGORY_KEY_MAP[k] ?? k;
}

const CATALOG_ITEM_BY_CAT_LABEL = new Map();
const CATALOG_ITEM_BY_LABEL = new Map();

for (const cat of ALL_CATEGORIES) {
  for (const sg of cat.subgroups) {
    for (const it of sg.items) {
      CATALOG_ITEM_BY_CAT_LABEL.set(`${cat.id}${ITEM_ICON_KEY_SEP}${it.label}`, it);
      if (!CATALOG_ITEM_BY_LABEL.has(it.label)) {
        CATALOG_ITEM_BY_LABEL.set(it.label, it);
      }
    }
  }
}

const ITEM_ICON_BY_CATEGORY_LABEL = (() => {
  const m = new Map();
  for (const cat of ALL_CATEGORIES) {
    for (const sg of cat.subgroups) {
      for (const it of sg.items) {
        m.set(`${cat.id}${ITEM_ICON_KEY_SEP}${it.label}`, it.icon ?? cat.emoji);
      }
    }
  }
  return m;
})();

/** @param {string} categoryKey @param {string} itemLabel */
export function getMingoServiceCatalogItem(categoryKey, itemLabel) {
  const catKey = normalizeCategoryKey(categoryKey);
  const label = isLegacyFoodMartLabel(itemLabel) ? FOOD_MART_ITEM_LABEL : String(itemLabel);
  return (
    CATALOG_ITEM_BY_CAT_LABEL.get(`${catKey}${ITEM_ICON_KEY_SEP}${label}`) ??
    CATALOG_ITEM_BY_LABEL.get(label) ??
    null
  );
}

/** @param {string} categoryKey @param {string} itemLabel @returns {string|null} */
export function resolveMingoServiceFavoriteRoute(categoryKey, itemLabel) {
  if (isLegacyFoodMartLabel(itemLabel)) return FOOD_MART_HUB_ROUTE;
  const item = getMingoServiceCatalogItem(categoryKey, itemLabel);
  return item?.hubRoute ?? null;
}

/**
 * @typedef {{ categoryKey: string, categoryLabel: string, itemLabel: string }} MingoServiceFavorite
 * @param {MingoServiceFavorite[]} favs
 */
export function normalizeMingoServiceFavorites(favs) {
  const mapped = favs.map((f) => ({
    ...f,
    categoryKey: normalizeCategoryKey(f.categoryKey),
    itemLabel: isLegacyFoodMartLabel(f.itemLabel) ? FOOD_MART_ITEM_LABEL : f.itemLabel,
  }));

  const foodMart = mapped.filter((f) => f.itemLabel === FOOD_MART_ITEM_LABEL);
  let list = mapped;
  if (foodMart.length > 1) {
    const keep = foodMart.find((f) => f.categoryKey === 'food_cafe_mart') ?? foodMart[0];
    list = mapped.filter((f) => f.itemLabel !== FOOD_MART_ITEM_LABEL);
    list.push(keep);
  }

  const seen = new Set();
  return list.filter((f) => {
    const k = `${f.categoryKey}::${f.itemLabel}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** @returns {MingoServiceFavorite[]} */
export function loadMingoServiceFavorites() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(MINGO_SERVICE_FAVORITES_KEY) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const filtered = parsed.filter(
      (x) =>
        x &&
        typeof x === 'object' &&
        typeof x.categoryKey === 'string' &&
        typeof x.categoryLabel === 'string' &&
        typeof x.itemLabel === 'string',
    );
    const normalized = normalizeMingoServiceFavorites(filtered);
    if (typeof window !== 'undefined' && JSON.stringify(normalized) !== JSON.stringify(filtered)) {
      window.localStorage.setItem(MINGO_SERVICE_FAVORITES_KEY, JSON.stringify(normalized));
      window.dispatchEvent(new CustomEvent('mingo-favorites-changed'));
    }
    return normalized;
  } catch {
    return [];
  }
}

/** 즐겨찾기 등 — 카탈로그 항목 라벨과 일치할 때 항목별 이모지 (AllServices와 동일 소스) */
export function getMingoServiceItemIcon(categoryKey, itemLabel) {
  if (!categoryKey || itemLabel == null || String(itemLabel).trim() === '') return null;
  const catKey = normalizeCategoryKey(categoryKey);
  const label = isLegacyFoodMartLabel(itemLabel) ? FOOD_MART_ITEM_LABEL : String(itemLabel);
  return ITEM_ICON_BY_CATEGORY_LABEL.get(`${catKey}${ITEM_ICON_KEY_SEP}${label}`) ?? null;
}
